/**
 * Projectile — player bullets, chomp flash, and the charging godzilla beam.
 *
 * One class handles three payload types via the `kind` enum:
 *   - `bullet`: a small fast white capsule fired horizontally right.
 *   - `chomp`: a short-lived triangular flash in front of the player (no
 *     collision body; purely visual + a single-frame hit-test against
 *     enemies via the scene's overlap detector).
 *   - `beam`: a tall vertical pillar held by the player; damages every enemy
 *     it overlaps while the button is held.
 *
 * Bullets and beams are pooled via Phaser.Physics.Arcade.Group in GameScene.
 */
import Phaser from "phaser";
import { TEX } from "../textures";

export type ProjectileKind = "bullet" | "chomp" | "beam";

export class Projectile extends Phaser.Physics.Arcade.Sprite {
  public kind: ProjectileKind = "bullet";
  public damage = 1;
  public speed = 720;
  
  public hitsRemaining = 1;
  public hitEnemies: Set<Phaser.GameObjects.GameObject> = new Set();

  constructor(scene: Phaser.Scene, x: number, y: number, texture?: string) {
    // Phaser's PhysicsGroup auto-spawns instances with just (scene, x, y) —
    // `texture` is undefined in that path. We fall back to the bullet texture
    // so the pooled instance has something to draw; GameScene.fireBullet()
    // overwrites the texture + config when it pulls an instance from the pool.
    super(scene, x, y, texture ?? TEX.BULLET);
  }

  /** Configure this instance as a player bullet and start moving. */
  public fireBullet(x: number, y: number): void {
    this.kind = "bullet";
    this.damage = 1;
    this.speed = 760;
    this.setTexture(TEX.BULLET);
    this.setActive(true);
    this.setVisible(true);
    this.setDepth(11);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    this.setPosition(x, y);
    this.setVelocity(this.speed, 0);
    this.setRotation(0);
    this.hitEnemies.clear();
  }

  /** Configure as a charging godzilla beam — held in place, horizontal. */
  public fireBeam(x: number, y: number): void {
    this.kind = "beam";
    this.damage = 4;
    this.setTexture(TEX.BEAM);
    this.setActive(true);
    this.setVisible(true);
    this.setDepth(11);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    // The beam texture is now drawn horizontally (240×28). We anchor the
    // left edge at the player's position so the beam extends to the right.
    this.setOrigin(0, 0.5);
    this.setPosition(x, y);
    this.setVelocity(0, 0);
    this.setRotation(0);
  }

  /** Configure as a chomp flash (visual only). */
  public fireChomp(x: number, y: number, facing: "left" | "right"): void {
    this.kind = "chomp";
    this.setTexture(TEX.CHOMP);
    this.setActive(true);
    this.setVisible(true);
    this.setDepth(11);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.enable = false;
    this.setOrigin(0.5, 0.5);
    this.setPosition(x, y);
    this.setVelocity(0, 0);
    this.setFlipX(facing === "left");
    this.setDisplaySize(200, 200);
    // Chomps linger for ~350ms; the iterate loop in GameScene reads the
    // bornAt data key to time them out.
  }

  public reset(): void {
    this.kind = "bullet";
    this.damage = 1;
    this.setActive(false);
    this.setVisible(false);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.enable = false;
    body.stop();
    this.setVelocity(0, 0);
    this.setRotation(0);
    this.setFlipX(false);
    this.setOrigin(0.5, 0.5);
    this.setScale(1.0);
    this.hitEnemies.clear();
  }
}